import { useEffect, useState } from 'react';
import { LEVELS_BY_KEY, TOPIC_HOST_LEVEL_KEYS } from '../courseLevels';
import { TopicIcon } from '../OutlineNode';
import * as api from '../api';

function nextAncestor(node, levelKey) {
   if(levelKey === 'topic') {
      for(const key of TOPIC_HOST_LEVEL_KEYS) {
         if(node[key]) return { levelKey: key, node: node[key] };
      }
      return null;
   }
   const level = LEVELS_BY_KEY[levelKey];
   if(!level || !level.parentField) return null;
   const parentNode = node[level.parentField];
   return parentNode ? { levelKey: level.parentField, node: parentNode } : null;
}

// One hand-built icon per hierarchy level (same 24x24-viewBox, stroke-only,
// line/rect/circle/polygon/polyline convention as everywhere else in this
// module - blockTypeIcons.jsx, OutlineNode's TopicIcon) so each breadcrumb
// segment reads as "what kind of thing is this" at a glance, not just a
// name in a chain - a real, deliberately distinct glyph per level, not one
// generic folder icon reused six times. No CourseIcon anymore - Course
// itself is never rendered (see PreviewBreadcrumb below).
function ModuleIcon() {
   return (
      <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
         <polygon points='12,3 21,8 12,13 3,8' />
         <polyline points='3,14 12,19 21,14' />
      </svg>
   );
}
function ChapterIcon() {
   return (
      <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
         <polygon points='6,2 18,2 18,22 12,17 6,22' />
      </svg>
   );
}
function UnitIcon() {
   return (
      <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
         <polygon points='12,3 21,8 21,16 12,21 3,16 3,8' />
         <line x1='12' y1='21' x2='12' y2='12' />
         <line x1='3' y1='8' x2='12' y2='12' />
         <line x1='21' y1='8' x2='12' y2='12' />
      </svg>
   );
}
function LessonIcon() {
   return (
      <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
         <circle cx='12' cy='12' r='9' />
         <line x1='12' y1='12' x2='12' y2='7' />
         <line x1='12' y1='12' x2='16' y2='14' />
      </svg>
   );
}
const LEVEL_ICONS = { module: ModuleIcon, chapter: ChapterIcon, unit: UnitIcon, lesson: LessonIcon, topic: TopicIcon };

function Chevron() {
   return (
      <svg className='course-preview-breadcrumb-chevron' viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
         <polyline points='9,6 15,12 9,18' />
      </svg>
   );
}

// One row of chips - Module/Chapter (the "primary" row) and Unit/Lesson/
// Topic (the "secondary" row, indented under it) both render through this,
// just with different level sets and a different rowClass for CSS to size
// up/down. `currentCrumb` (always the last item of the FULL original path,
// not necessarily the last item of THIS row) decides which single chip
// across both rows gets the accent-highlighted treatment.
function BreadcrumbRow({ items, currentCrumb, onSelect, rowClass }) {
   return (
      <div className={'course-preview-breadcrumb-row ' + rowClass}>
         {items.map((crumb, index) => {
            const isCurrent = crumb === currentCrumb;
            const clickable = !isCurrent;
            const level = LEVELS_BY_KEY[crumb.levelKey];
            const Icon = LEVEL_ICONS[crumb.levelKey] || TopicIcon;
            return (
               <span className='course-preview-breadcrumb-segment' key={crumb.levelKey + '-' + crumb.id}>
                  {index > 0 ? <Chevron /> : ''}
                  <div className={'course-preview-breadcrumb-crumb course-preview-breadcrumb-crumb-' + crumb.levelKey
                     + (isCurrent ? ' current' : '') + (clickable ? ' clickable' : '')}
                     onClick={clickable ? () => onSelect(crumb) : undefined}>
                     <span className='course-preview-breadcrumb-icon'><Icon /></span>
                     <span className='course-preview-breadcrumb-text'>
                        <span className='course-preview-breadcrumb-kind'>{level.label}</span>
                        <span className='course-preview-breadcrumb-name' title={crumb.name}>{crumb.name}</span>
                     </span>
                  </div>
               </span>
            );
         })}
      </div>
   );
}

// Shows exactly where whatever's selected in the tree sits in the course
// hierarchy. Walks the selected node's own nested parent chain up to the
// root from ONE GET: every relation on this backend nests its own parent
// recursively (confirmed live - a Topic's GET response nests
// lesson->unit->chapter->module->course all the way up), so no per-level
// fetch is needed here.
//
// Course itself is walked (still needed internally as Module's parentId
// when jumping straight to a Module) but never RENDERED - per explicit
// user ask, which course this is is already obvious everywhere else in
// this admin tool, so repeating it in every single breadcrumb is noise.
//
// The remaining levels split into two stacked rows rather than one long
// inline chain, sized to show importance at a glance: Module/Chapter are
// the "primary" row (Module biggest, Chapter a step down but still bigger
// than the rest) since they're the structural headline; Unit/Lesson/Topic
// (whichever are actually present) are a smaller "secondary" row
// underneath, slightly indented to read as continuing from the row above.
// The split is level-key-driven, not positional, so it degrades cleanly
// for any real path shape (a Topic straight on a Module has no Chapter at
// all; a Topic straight on the Course has neither, so only the secondary
// row renders) - whichever row ends up empty just doesn't render.
//
// Only the "middle" ancestors are clickable - the currently-selected node
// itself is just "where you already are" (clicking would no-op or desync
// the Constructor tab's own selection), so it renders as a plain,
// non-interactive, accent-highlighted chip instead.
export default function PreviewBreadcrumb({ selected, actions }) {
   const [path, setPath] = useState(null);

   useEffect(() => {
      if(!selected) { setPath(null); return; }
      let cancelled = false;
      const level = LEVELS_BY_KEY[selected.levelKey];
      api.getOne(level.apiBase, selected.id).then(response => {
         if(cancelled) return;
         const node = response.data;
         const trail = [{ levelKey: selected.levelKey, id: node.id, name: node.name }];
         let cursor = nextAncestor(node, selected.levelKey);
         while(cursor) {
            trail.unshift({ levelKey: cursor.levelKey, id: cursor.node.id, name: cursor.node.name });
            cursor = nextAncestor(cursor.node, cursor.levelKey);
         }
         setPath(trail);
      }).catch(() => setPath(null));
      return () => { cancelled = true; };
   }, [selected]);

   if(!path || path.length === 0) return null;

   const handleClick = (crumb) => {
      if(!actions) return;
      const index = path.indexOf(crumb);
      const parent = path[index - 1];
      const hostLevelKey = crumb.levelKey === 'topic' ? (parent && parent.levelKey) : undefined;
      actions.select(crumb.levelKey, crumb.id, parent && parent.id, hostLevelKey);
   };

   const currentCrumb = path[path.length - 1];
   const visible = path.filter(c => c.levelKey !== 'course');
   const primaryRow = visible.filter(c => c.levelKey === 'module' || c.levelKey === 'chapter');
   const secondaryRow = visible.filter(c => c.levelKey !== 'module' && c.levelKey !== 'chapter');

   return (
      <div className='course-preview-breadcrumb'>
         {primaryRow.length > 0 ? (
            <BreadcrumbRow items={primaryRow} currentCrumb={currentCrumb} onSelect={handleClick} rowClass='course-preview-breadcrumb-row-primary' />
         ) : ''}
         {secondaryRow.length > 0 ? (
            <BreadcrumbRow items={secondaryRow} currentCrumb={currentCrumb} onSelect={handleClick}
               rowClass={'course-preview-breadcrumb-row-secondary' + (primaryRow.length === 0 ? ' standalone' : '')} />
         ) : ''}
      </div>
   );
}
